import React, { useState, useEffect } from "react";
// GraphQL
import CREATE_SURVEY from "graphql/mutations/CreateSurvey";
import CREATE_SURVEY_RESPONSE from "graphql/mutations/CreateSurveyResponse";
// Libraries
import { useMutation } from "@apollo/client";
// Hooks
import useErrors from "hooks/useErrors";
// Components
import StudentAge from "./StudentAge";
import ReasonForLearning from "./ReasonForLearning";
import PreviousExperience from "./PreviousExperience";
import LearningGoal from "./LearningGoal";
import TimeCommitment from "./TimeCommitment";
import ReferralSource from "./ReferralSource";

interface RegistrationSurveyProps {
  onComplete: () => void;
  onSurveyCreated: (surveyId: number) => void;
  studyLanguageId: string;
}

const RegistrationSurvey = ({
  onComplete,
  onSurveyCreated,
  studyLanguageId,
}: RegistrationSurveyProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [registrationSurveyId, setRegistrationSurveyId] = useState<
    number | null
  >(null);

  const { handleError } = useErrors();
  const handleNext = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const [survey] = useMutation(CREATE_SURVEY);
  const [createSurveyResponse] = useMutation(CREATE_SURVEY_RESPONSE);

  // CREATE New registration survey
  const handleCreateSurvey = React.useCallback(async () => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const surveyType = "REGISTRATION";
    if (detectedTimezone && surveyType && studyLanguageId !== "") {
      try {
        const {
          data: {
            survey: {
              survey: { id },
            },
          },
        } = await survey({
          variables: {
            input: {
              timeZone: detectedTimezone,
              surveyType,
              studentId: null,
              languageId: studyLanguageId,
            },
          },
        });

        onSurveyCreated(id);
        setRegistrationSurveyId(id);
        return id;
      } catch (e) {
        handleError(e);
        return null;
      }
    } else {
      return null;
    }
  }, [survey, handleError, onSurveyCreated, studyLanguageId]);

  // Create survey response
  const handleCreateSurveyResponse = React.useCallback(
    async (surveyQuestionId, surveyAnswerId) => {
      try {
        await createSurveyResponse({
          variables: {
            input: {
              attributes: {
                surveyQuestionId,
                surveyAnswerId,
                registrationSurveyId,
                studentId: null,
              },
            },
          },
        });
      } catch (e) {
        console.error("Error saving survey response:", e);
      }
    },
    [createSurveyResponse, registrationSurveyId]
  );

  useEffect(() => {
    if (studyLanguageId !== "") {
      handleCreateSurvey();
    }
  }, [studyLanguageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = [
    <StudentAge
      onChange={handleNext}
      createSurveyResponse={handleCreateSurveyResponse}
    />,
    <ReasonForLearning
      onChange={handleNext}
      createSurveyResponse={handleCreateSurveyResponse}
      language={studyLanguageId}
    />,
    <PreviousExperience
      onChange={handleNext}
      createSurveyResponse={handleCreateSurveyResponse}
      language={studyLanguageId}
    />,
    <LearningGoal
      onChange={handleNext}
      createSurveyResponse={handleCreateSurveyResponse}
    />,
    <TimeCommitment
      onChange={handleNext}
      createSurveyResponse={handleCreateSurveyResponse}
    />,
    <ReferralSource
      onChange={handleNext}
      onComplete={onComplete}
      createSurveyResponse={handleCreateSurveyResponse}
    />,
  ];

  return <div>{steps[currentStep]}</div>;
};

export default RegistrationSurvey;
